import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
  Switch,
} from '@mui/material';
import { InfoIcon, CircleCheckBig, Copy } from 'lucide-react';

import Tooltip from 'components/tooltip';
import IconButton from 'components/icon-button';
import { CompareSummary, Summary } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import KebabMenu from 'components/kebab-menu';
import ScenarioCloneDialog from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-clone-dialog';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import ScenarioResultFilters from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/result-sites/scenario-result-filters';
import {
  SELECT_ALL_KPI_ID,
  buildScenarioAwardBadges,
  buildAvailableFishGroupIds,
  buildCompareFeedPrograms,
  buildFishGroupsFromSummaries,
  buildScenarioResultFromSelections,
  getVisibleAwardKeys,
} from 'views/bluviu-nutrition/features/scenarios-v2/utils/compare-result-utils';
import { useCompareFilters } from 'views/bluviu-nutrition/features/scenarios-v2/hooks/use-compare-filters';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import ScenarioAwardIcons from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/scenario-award-icons';
import FormatMetricValue from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/format-metric-value';
import _ from 'underscore';
import ScenarioFeedTooltip from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/scenario-result-feed-tooltip';
import { FormattedNumber, useIntl } from 'react-intl';

const cloneFeedProgramLabel = 'Clone feed program';

type TableColumnDefinition = {
  key: string;
  labelId: string;
  hasInfo: boolean;
  tooltipId?: string;
};

const tableColumnDefinitions: TableColumnDefinition[] = [
  { key: 'isSelected', labelId: 'bluViuNutrition.viewScenarioResultGt2.compareTable.reference', hasInfo: false },
  {
    key: 'finalWeightGrams',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.finalWeightGrams',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.finalWeightGrams',
  },
  {
    key: 'weightGainGrams',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.weightGainGrams',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.weightGainGrams',
  },
  {
    key: 'hogGainGrams',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.hogGainGrams',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.hogGainGrams',
  },
  {
    key: 'daysToReach',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.daysToReach',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.daysToReach',
  },
  {
    key: 'feedIntakeGrams',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.feedIntakeGrams',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.feedIntakeGrams',
  },
  { key: 'bFcr', labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.bFcr', hasInfo: true, tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.bFcr' },
  { key: 'eFcr', labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.eFcr', hasInfo: true, tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.eFcr' },
  {
    key: 'carcassYieldPercent',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.carcassYieldPercent',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.carcassYieldPercent',
  },
  {
    key: 'filletFatPercent',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.filletFatPercent',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.filletFatPercent',
  },
  {
    key: 'averageFeedCostPerKgFeed',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.averageFeedCostPerKgFeed',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.averageFeedCostPerKgFeed',
  },
  {
    key: 'feedCostOfGrowth',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.feedCostOfGrowth',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.feedCostOfGrowth',
  },
  {
    key: 'feedCostOfHogGain',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.feedCostOfHogGain',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.feedCostOfHogGain',
  },
  {
    key: 'feedCostTotal',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.feedCostTotal',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.feedCostTotal',
  },
  {
    key: 'operationsCostTotal',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.operationsCostTotal',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.operationsCostTotal',
  },
  {
    key: 'smoltCostTotal',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.smoltCostTotal',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.smoltCostTotal',
  },
  {
    key: 'harvestCostTotal',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.harvestCostTotal',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.harvestCostTotal',
  },
  {
    key: 'treatmentCostTotal',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.treatmentCostTotal',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.treatmentCostTotal',
  },
  {
    key: 'totalCost',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.totalCost',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.totalCost',
  },
  {
    key: 'potentialSalesValue',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.potentialSalesValue',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.potentialSalesValue',
  },
  {
    key: 'potentialProfit',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.potentialProfit',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.potentialProfit',
  },
  {
    key: 'globalWarmingPotentialCo2KgPerKgFeed',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.globalWarmingPotentialCo2KgPerKgFeed',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.globalWarmingPotentialCo2KgPerKgFeed',
  },
  {
    key: 'globalWarmingPotentialCo2KgPerKgGrowth',
    labelId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.globalWarmingPotentialCo2KgPerKgGrowth',
    hasInfo: true,
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.globalWarmingPotentialCo2KgPerKgGrowth',
  },
];

export const CompareTable: React.FC = () => {
  const intl = useIntl();
  const theme = useTheme();
  const prevProgramIdsRef = useRef<string>('');
  const prevFishGroupIdsRef = useRef<string>('');
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  const {
    getScenarioPostProcessQuery: { data: postProcessData },
    selectedPrograms,
  } = useScenarioResultState();
  const hasOverviewSelection = selectedPrograms.length > 0;
  const currency = postProcessData?.currency;

  const [feedPrograms, setFeedPrograms] = useState<CompareSummary[]>([]);
  const [referenceProgramId, setReferenceProgramId] = useState<string | null>(null);

  // #region Memoized data and computations
  const siteSummaries = useMemo<Summary[]>(() => postProcessData?.siteSummary ?? [], [postProcessData?.siteSummary]);
  const fishGroupSummaries = useMemo(() => postProcessData?.fishGroupSummaries ?? [], [postProcessData?.fishGroupSummaries]);
  const fishGroups = useMemo(() => buildFishGroupsFromSummaries(fishGroupSummaries), [fishGroupSummaries]);
  const availableFishGroupIds = useMemo(() => buildAvailableFishGroupIds(fishGroups), [fishGroups]);

  const {
    filters,
    appliedFilters,
    updateFilters,
    updateImmediateFilters,
    resetFilters,
    initializeFeedPrograms,
    initializeResultsFor,
    applyFilters,
  } = useCompareFilters();

  const { appliedFeedPrograms, appliedKPIs, appliedResultsFor } = appliedFilters;

  const scenarioResult = useMemo(
    () => buildScenarioResultFromSelections(siteSummaries, fishGroupSummaries, appliedResultsFor),
    [siteSummaries, fishGroupSummaries, appliedResultsFor]
  );

  const columns = useMemo(() => {
    const translatedColumns = tableColumnDefinitions.map((column) => ({
      ...column,
      label: intl.formatMessage({ id: column.labelId }),
      tooltip: column.tooltipId ? intl.formatMessage({ id: column.tooltipId }) : undefined,
    }));

    if (appliedKPIs.length === 0 || appliedKPIs.includes(SELECT_ALL_KPI_ID)) {
      return translatedColumns;
    }
    return translatedColumns.filter((col) => col.key === 'isSelected' || appliedKPIs.includes(col.key));
  }, [appliedKPIs, intl]);

  const visibleFeedPrograms = useMemo(
    () => feedPrograms.filter((program) => appliedFeedPrograms.includes(program.id)),
    [appliedFeedPrograms, feedPrograms]
  );

  // #endregion

  // #region Effects
  useEffect(() => {
    const rebuiltFeedPrograms = buildCompareFeedPrograms(scenarioResult);
    setFeedPrograms(rebuiltFeedPrograms);
    setReferenceProgramId((prevId) => (prevId && rebuiltFeedPrograms.some((p) => p.id === prevId) ? prevId : null));
  }, [scenarioResult]);

  useEffect(() => {
    const availableProgramIds = feedPrograms.map((p) => p.id);
    if (!availableProgramIds.length) return;

    const selectedFromOverview = _.uniq(selectedPrograms.filter((id) => availableProgramIds.includes(id)));
    const programsToInitialize = hasOverviewSelection ? selectedFromOverview : availableProgramIds;
    const selectionKey = programsToInitialize.slice().sort().join(',');

    if (selectionKey === prevProgramIdsRef.current) return;

    prevProgramIdsRef.current = selectionKey;
    initializeFeedPrograms(programsToInitialize);
  }, [feedPrograms, hasOverviewSelection, initializeFeedPrograms, selectedPrograms]);

  useEffect(() => {
    const currentIds = availableFishGroupIds.join(',');
    if (!currentIds || currentIds === prevFishGroupIdsRef.current) return;

    prevFishGroupIdsRef.current = currentIds;
    initializeResultsFor(availableFishGroupIds);
  }, [availableFishGroupIds, initializeResultsFor]);

  const handleToggleProgram = (id: string) => {
    setReferenceProgramId((prevId) => (prevId === id ? null : id));
  };

  const handleApplyFilters = () => {
    applyFilters();
  };

  // #endregion

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <ScenarioResultFilters
        feedPrograms={feedPrograms}
        fishGroups={fishGroups}
        handleApplyFilters={handleApplyFilters}
        filters={filters}
        updateFilters={updateFilters}
        updateImmediateFilters={updateImmediateFilters}
        clearFilters={() =>
          resetFilters(
            feedPrograms.map((program) => program.id),
            availableFishGroupIds
          )
        }
      />

      <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={1}>
        <InfoIcon size={16} />
        <Typography variant="body1" sx={{ fontWeight: 400 }}>
          Showing averaged data for the selected fish groups.
        </Typography>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          bgcolor: theme.palette.lightBlue.light,
          borderRadius: 2,
          maxWidth: '100%',
          overflowX: 'auto',
          ['&.MuiPaper-root']: {
            padding: theme.spacing(0),
          },
        }}>
        <Table sx={{ borderCollapse: 'separate', tableLayout: 'auto', width: '100%', minWidth: 'max-content' }}>
          <TableBody>
            <TableRow sx={{ boxShadow: theme.shadows[2] }}>
              <TableCell
                sx={{
                  bgcolor: theme.palette.common.white,
                  border: 'none',
                  verticalAlign: 'middle',
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  boxShadow: theme.shadows[1],
                }}>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'flex-start'}
                  alignItems={'flex-start'}
                  width={'100%'}
                  gap={1}>
                  <ScenarioAwardIcons visibleAwardKeys={[]} showPlaceholder />
                  <Typography variant="subtitle2" sx={{ color: theme.palette.darkBlue.o75, fontWeight: 500 }}>
                    {appliedFeedPrograms.filter((id) => id !== 'all').length} feed programs
                  </Typography>
                </Box>
              </TableCell>
              {visibleFeedPrograms.map((program) => {
                const visibleAwardKeys = getVisibleAwardKeys(buildScenarioAwardBadges(program, visibleFeedPrograms));
                const isReference = program.id === referenceProgramId;

                return (
                  <TableCell
                    key={program.id}
                    sx={{
                      bgcolor: isReference ? theme.palette.lightBlue.o25 : theme.palette.common.white,
                      border: 'none',
                      borderLeft: `1px solid ${theme.palette.gray.o25}`,
                      verticalAlign: 'top',
                    }}>
                    <Box display={'flex'} flexDirection={'column'} gap={6} justifyContent={'space-between'} alignItems={'center'}>
                      <ScenarioAwardIcons visibleAwardKeys={visibleAwardKeys} showPlaceholder />
                      <Box
                        width={'100%'}
                        display={'flex'}
                        flexDirection={'row'}
                        justifyContent={'space-between'}
                        alignItems={'center'}>
                        <ScenarioFeedTooltip feeds={program.feeds} feedPlan={program.name} display={'both'} />
                        <KebabMenu dark>
                          <Box
                            display={'flex'}
                            flexDirection={'column'}
                            gap={1}
                            justifyContent={'flex-start'}
                            alignItems={'flex-start'}>
                            <Button
                              variant="text"
                              startIcon={<CircleCheckBig size={20} />}
                              sx={{
                                padding: theme.spacing(0),
                                color: theme.palette.darkBlue.main,
                                textTransform: 'none',
                              }}>
                              Apply to site
                            </Button>
                            {scenario && (
                              <ScenarioCloneDialog
                                button={
                                  <Tooltip title={cloneFeedProgramLabel}>
                                    <IconButton mr={0} aria-label={cloneFeedProgramLabel} sx={{ color: theme.palette.darkBlue.main }}>
                                      <Copy size={20} />
                                    </IconButton>
                                  </Tooltip>
                                }
                                scenario={scenario}
                                selectedFeedProgramIds={[program.feedProgramId]}
                              />
                            )}
                          </Box>
                        </KebabMenu>
                      </Box>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
            {columns.map((metric, index) => {
              if (metric.key === 'isSelected' && visibleFeedPrograms.length === 1) return null;

              const rowBackgroundColor = index % 2 === 0 ? theme.palette.lightBlue.light : theme.palette.common.white;

              return (
                <TableRow key={metric.key} sx={{ bgcolor: rowBackgroundColor }}>
                  <TableCell
                    sx={{
                      border: 'none',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: rowBackgroundColor,
                      boxShadow: theme.shadows[1],
                    }}>
                    {metric.hasInfo ? (
                      <Tooltip title={<Typography fontWeight="bold">{metric.tooltip}</Typography>} placement="right-end">
                        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={2}>
                          <Typography fontWeight="bold" sx={{ wordBreak: 'break-all' }}>
                            {metric.label}
                          </Typography>
                          <InfoIcon size={16} />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography fontWeight="normal" sx={{ marginBottom: theme.spacing(2) }}>
                        {metric.label}
                      </Typography>
                    )}
                  </TableCell>
                  {feedPrograms
                    .filter((program) => appliedFeedPrograms.includes(program.id))
                    .map((program) => {
                      const rawValue = program[metric.key as keyof Summary];
                      const value = typeof rawValue === 'number' ? rawValue : 0;
                      const baselineProgram = referenceProgramId ? feedPrograms.find((p) => p.id === referenceProgramId) : null;
                      const isReference = program.id === referenceProgramId;
                      const baseValue = baselineProgram ? Number(baselineProgram[metric.key as keyof Summary]) : 0;
                      const absoluteDiff = value - baseValue;
                      const relativeDiff = ((value - baseValue) / baseValue) * 100;

                      return (
                        <TableCell
                          key={program.id}
                          sx={{
                            bgcolor: isReference ? theme.palette.lightBlue.o25 : rowBackgroundColor,
                            border: 'none',
                            borderLeft: `1px solid ${theme.palette.gray.o25}`,
                          }}>
                          {metric.key === 'isSelected' ? (
                              <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                                <Switch checked={isReference} onChange={() => handleToggleProgram(program.id)} />
                              </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2">
                                <FormatMetricValue value={value} valueKey={metric.key} currency={currency} />
                              </Typography>
                              {referenceProgramId ? (
                                isReference ? (
                                  <Typography variant="body2" sx={{ color: theme.palette.darkBlue.o75, marginLeft: theme.spacing(1) }}>
                                    {'(0)'}
                                  </Typography>
                                ) : (
                                  <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                                    <Typography variant="body2" sx={{ color: theme.palette.darkBlue.o75, marginLeft: theme.spacing(1) }}>
                                      ({absoluteDiff >= 0 ? '+' : ''}
                                      {<FormattedNumber value={relativeDiff} maximumFractionDigits={1} />}%)
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.darkBlue.o75, marginLeft: theme.spacing(1) }}>
                                      ({absoluteDiff >= 0 ? '+' : ''}
                                      <FormatMetricValue value={absoluteDiff} valueKey={metric.key} currency={currency} />)
                                    </Typography>
                                  </Box>

                                )
                              ) : null}
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default React.memo(CompareTable);
